import React from 'react'

import {
    ApplePayIcon,
    BankIcon,
    CreditCardIcon,
    GooglePayIcon,
    PayPalIcon,
    WalletIcon,
} from '@src/components/icons/PaymentMethods'
import { PaymentMethod } from '@src/types/coinbase'

/**
 * Options for the payment method icon
 */
interface PaymentMethodIconOptions {
    /** CSS class to apply to the icon */
    className?: string
    /** Width of the icon */
    width?: number
    /** Height of the icon */
    height?: number
}

/**
 * Get the appropriate icon based on payment method ID
 * @param method The payment method or undefined
 * @param options Icon styling options
 * @returns React element with the appropriate icon
 */
export const getPaymentMethodIcon = (method?: PaymentMethod, options: PaymentMethodIconOptions = {}) => {
    const { className = 'text-mach-color-text-secondary', width, height } = options
    const props = { className, ...(width ? { width } : {}), ...(height ? { height } : {}) }

    // If no method provided, return default credit card icon
    if (!method) return <CreditCardIcon {...props} />

    const id = method.id.toLowerCase()

    if (id.includes('bank') || id.includes('ach')) {
        return <BankIcon {...props} />
    }
    if (id.includes('apple')) {
        return <ApplePayIcon {...props} />
    }
    if (id.includes('google')) {
        return <GooglePayIcon {...props} />
    }
    if (id.includes('paypal')) {
        return <PayPalIcon {...props} />
    }
    if (id.includes('wallet') || id.includes('crypto')) {
        return <WalletIcon {...props} />
    }

    // Default to credit card icon
    return <CreditCardIcon {...props} />
}

// Human-readable names for payment methods
const PAYMENT_METHOD_NAMES: Record<string, string> = {
    APPLE_PAY: 'Apple Pay',
    CARD: 'Credit Card',
    ACH_BANK_TRANSFER: 'ACH Bank Transfer',
    BANK_WIRE: 'Bank Wire',
    GOOGLE_PAY: 'Google Pay',
    WALLET: 'Digital Wallet',
    BANK_ACCOUNT: 'Bank Account',
    PAYPAL: 'PayPal',
    VENMO: 'Venmo',
}

/**
 * Get formatted display name for a payment method
 * @param method The payment method or undefined
 * @returns Formatted display name string
 */
export const getPaymentMethodDisplayName = (method?: PaymentMethod): string => {
    if (!method) return 'Select Method'

    // First check the mapping for a predefined name
    const methodKey = method.id.toUpperCase()
    if (PAYMENT_METHOD_NAMES[methodKey]) {
        return PAYMENT_METHOD_NAMES[methodKey]
    }

    // Convert snake_case to Title Case with spaces
    return method.id
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
}
